import { useState, useRef, useEffect } from "react";
import LocalLink from "@/components/LocalLink";
import { ChevronDown, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const locations = [
  { labelKey: "nav.locations.ny", subtitleKey: "nav.locations.ny.sub", href: "/locations/new-york" },
  { labelKey: "nav.locations.nj", subtitleKey: "nav.locations.nj.sub", href: "/locations/new-jersey" },
  { labelKey: "nav.locations.fl", subtitleKey: "nav.locations.fl.sub", href: "/locations/florida" },
];

const LocationsDropdown = ({ mobile = false, onNavigate }: { mobile?: boolean; onNavigate?: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (mobile) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (mobile) return;
    timeoutRef.current = setTimeout(() => setIsOpen(false), 250);
  };

  // ─── MOBILE: Accordion ───
  if (mobile) {
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between w-full min-h-[48px] py-2 pl-2 text-base text-white font-medium hover:text-gold transition-colors"
          aria-expanded={isOpen}
        >
          {t("nav.locations")}
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
        {isOpen && (
          <div className="mt-2 mb-3 ml-2 space-y-0.5 rounded-lg bg-white/[0.03] p-2 motion-safe:animate-fade-in">
            {locations.map((loc) => (
              <LocalLink
                key={loc.href}
                to={loc.href}
                onClick={() => { setIsOpen(false); onNavigate?.(); }}
                className="flex items-center gap-2 min-h-[44px] py-2 px-2 rounded-md text-white/70 hover:text-gold hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0"
              >
                <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{t(loc.labelKey)}</span>
                    {(loc as any).comingSoon && (
                      <span className="text-[10px] font-semibold text-gold bg-gold/10 px-1.5 py-0.5 rounded">{t("common.comingSoon")}</span>
                    )}
                  </div>
                  <span className="block text-xs text-white/50">{t(loc.subtitleKey)}</span>
                </div>
              </LocalLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── DESKTOP: Dropdown ───
  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className="flex items-center gap-1 text-sm font-medium text-white/80 hover:text-gold transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        {t("nav.locations")}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[280px] bg-navy/95 backdrop-blur-2xl rounded-xl shadow-[0_16px_48px_rgba(0,0,0,0.35)] border border-white/10 overflow-hidden animate-fade-in z-50">
          {/* Top accent line */}
          <div className="h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent" />

          <div className="p-3 space-y-0.5">
            {locations.map((loc) => (
              <LocalLink
                key={loc.href}
                to={loc.href}
                onClick={() => { setIsOpen(false); onNavigate?.(); }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/80 hover:text-white hover:bg-white/5 transition-colors group"
              >
                <div className="w-8 h-8 rounded-md bg-gold/10 flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                  <MapPin className="w-4 h-4 text-gold" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium block">{t(loc.labelKey)}</span>
                    {(loc as any).comingSoon && (
                      <span className="text-[9px] font-semibold text-gold bg-gold/10 px-1.5 py-0.5 rounded leading-none">{t("common.comingSoon")}</span>
                    )}
                  </div>
                  <span className="text-[11px] text-white/40 block">{t(loc.subtitleKey)}</span>
                </div>
              </LocalLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsDropdown;
